import clsx from 'clsx';
import styles from './CategorySelector.module.scss';

const PILL_VARIANT_CLASS = {
    green: styles.PillGreen,
    grey: styles.PillGrey,
    red: styles.PillRed,
};

// Trailing word in the count pill, e.g. "{count} Open" — defaults to 'Files' so callers that
// don't pass `countLabel` keep rendering exactly as before.
const DEFAULT_PILL_COUNT_LABEL = 'Files';

type Props = {
    readonly categoryOptions: ReadonlyArray<{
        readonly id: string;
        readonly label: string;
        readonly count?: number;
        readonly disabled?: boolean;
        // Opt-in: renders count as a "{count} {countLabel}" pill (countLabel defaults to 'Files')
        // in the given color instead of the default plain grey number. Omit pillVariant to keep
        // every other CategorySelector caller unchanged.
        readonly pillVariant?: 'green' | 'grey' | 'red';
        readonly countLabel?: string;
    }>;
    readonly selectedCategory: string | null;
    readonly changeSelectedTab: (category: string) => void;
    style?: React.CSSProperties;
    categoryStyle?: React.CSSProperties;
    equalWidth?: boolean;
};

function CategorySelector({ categoryOptions, selectedCategory, changeSelectedTab, style, categoryStyle, equalWidth }: Props) {
    return (
        <div className={clsx(styles.CategorySelector, { [styles.EqualWidth]: equalWidth })} style={style}>
            {categoryOptions.map((category) => (
                <div
                    className={clsx(styles.Category, {
                        [styles.SelectedCategory]: selectedCategory === category.id,
                        [styles.DisabledCategory]: category.disabled,
                    })}
                    key={category.id}
                    // Toggle buttons rather than a tablist: several consumers use this as a filter or
                    // form input with no associated panel, so `aria-controls` could not be honoured.
                    role="button"
                    aria-pressed={selectedCategory === category.id}
                    aria-disabled={category.disabled || undefined}
                    tabIndex={category.disabled ? undefined : 0}
                    onClick={() => !category.disabled && changeSelectedTab(category.id)}
                    onKeyDown={(e) => {
                        if (category.disabled || (e.key !== 'Enter' && e.key !== ' ')) return;
                        e.preventDefault();
                        changeSelectedTab(category.id);
                    }}
                    style={categoryStyle}
                >
                    {category.label}
                    {category.count !== undefined &&
                        (category.pillVariant ? (
                            <span className={clsx(styles.CountPill, PILL_VARIANT_CLASS[category.pillVariant])}>
                                {category.count} {category.countLabel ?? DEFAULT_PILL_COUNT_LABEL}
                            </span>
                        ) : (
                            <span className={styles.grey}>&nbsp;{category.count}</span>
                        ))}
                </div>
            ))}
        </div>
    );
}

export default CategorySelector;
